import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import extendedWarrantyService from "@/services/extendedWarrantyService";
import individualCustomersService from "@/services/individualCustomersService";

import GeneralSummaryCard from "./generalSummaryCard";

import { Receipt, Users } from "lucide-react";

/**
 * Alt bayi (merchant_user.type = STORE) için Genel Bakış özeti.
 *
 * Genel Bakış'ın geri kalanı merchant geneli rakamlardan oluşuyor ve bayi kırılımı
 * yok: ürün kartları katalog sayısı (product-api, merchant_product), kredi bakiyesi
 * merchant seviyesinde, garanti gönderimleri grafiği ve tablolar ise
 * individual_customer_product üzerinden — o tabloda merchant_user_id kolonu olmadığı
 * için bayiye göre daraltılamıyor. Bu yüzden bayiye o rakamlar hiç gösterilmiyor.
 *
 * Buradaki iki sayı ayrı bir uçtan değil, mevcut listeleme uçlarının
 * totalElements'inden okunuyor: EPS-4974 ile bu iki uç zaten STORE kullanıcıya göre
 * daraltıldığı için sonuç tanımı gereği bayinin kendi kayıtları. İçerik
 * kullanılmadığından size=1 isteniyor.
 */
const StoreSummarySection = () => {
  const { t } = useTranslation();

  const [counts, setCounts] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const countOf = (promise) =>
      promise.then((data) => data?.totalElements ?? null).catch(() => null);

    Promise.all([
      countOf(extendedWarrantyService.getExtendedWarranties({ page: 1, size: 1 })),
      countOf(individualCustomersService.getIndividualCustomers({ page: 1, size: 1 })),
    ]).then(([sales, customers]) => {
      if (!cancelled) setCounts({ sales, customers });
    });

    return () => {
      cancelled = true;
    };
  }, []);

  // Uç hata verirse kart yerinde kalsın; sıfır göstermek "hiç satışın yok" demek olur.
  const display = (value) =>
    value == null ? "-" : Number(value).toLocaleString();

  if (!counts) return <StoreSummarySectionSkeleton />;

  return (
    <section className="flex flex-col gap-4">
      <h2 className="text-lg font-medium text-[#111729]">
        {t("overviewPage.generalSummary")}
      </h2>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <GeneralSummaryCard>
          <GeneralSummaryCard.Content icon={Receipt}>
            <h2 className="text-sm font-semibold text-[#677389]">
              {t("overviewPage.store.mySales", "Satışlarım")}
            </h2>
            <h3 className="text-xl font-semibold text-[#111729]">
              {display(counts.sales)}
            </h3>
          </GeneralSummaryCard.Content>
        </GeneralSummaryCard>

        <GeneralSummaryCard>
          <GeneralSummaryCard.Content icon={Users}>
            <h2 className="text-sm font-semibold text-[#677389]">
              {t("overviewPage.store.myCustomers", "Müşterilerim")}
            </h2>
            <h3 className="text-xl font-semibold text-[#111729]">
              {display(counts.customers)}
            </h3>
          </GeneralSummaryCard.Content>
        </GeneralSummaryCard>
      </div>
    </section>
  );
};

const StoreSummarySectionSkeleton = () => (
  <section aria-hidden="true" aria-busy="true" className="flex flex-col gap-4">
    <div className="h-6 w-48 rounded bg-gray-300"></div>
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      {[0, 1].map((key) => (
        <div
          key={key}
          className="animate-pulse overflow-hidden rounded-lg border border-[#EAECF0] bg-white px-3 py-6 shadow-sm"
        >
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-50">
              <div className="h-5 w-5 rounded bg-gray-300"></div>
            </div>
            <div className="flex flex-col items-start text-left">
              <div className="mb-1 h-4 w-20 rounded bg-gray-300"></div>
              <div className="h-6 w-12 rounded bg-gray-300"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  </section>
);

export default StoreSummarySection;
